import { useNavigate } from 'react-router-dom';
/**
 * UserAccount.js  —  ClubSwap User Account System
 *
 * Auth:     Firebase Auth (email + password) — stays as-is.
 * Balance:  MongoDB via Express API — authoritative.
 * History:  MongoDB via Express API — authoritative.
 * Withdraw: MongoDB via Express API — authoritative.
 * Deposit:  User sends crypto; a Firebase pending record is created for admin
 *           to confirm on-chain and credit via POST /api/wallet/deposit.
 */
import LinkTelegram from './LinkTelegram'
import React, { useEffect, useState, useCallback } from 'react'
import { initializeApp, getApps }                  from 'firebase/app'
import { getDatabase, ref, set, get, update, onValue, off } from 'firebase/database'
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
} from 'firebase/auth'
import {
    authAPI, walletAPI,
    setToken, setMongoId, clearToken, getToken,
} from '../api/api'

// ─── Firebase init ────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

let _app = null, _auth = null, _db = null
function getFirebase() {
    if (!_app) {
        const existing = getApps()
        _app  = existing.length ? existing[0] : initializeApp(FIREBASE_CONFIG)
        _auth = getAuth(_app)
        _db   = getDatabase(_app)
    }
    return { auth: _auth, db: _db }
}

// ─── Constants ────────────────────────────────────────────────────────────────
const DEPOSIT_ADDRESSES = {
    ERC20: '0xYourERC20DepositWalletAddressHere',
    BASE:  '0xYourBASEDepositWalletAddressHere',
}
const DIAMOND_PACKAGES = [
    { diamonds: 10,   usdPrice: 1.00  },
    { diamonds: 50,   usdPrice: 4.50  },
    { diamonds: 100,  usdPrice: 8.00  },
    { diamonds: 500,  usdPrice: 35.00 },
    { diamonds: 1000, usdPrice: 65.00 },
]

const NETWORKS = ['Polygon POS', 'BEP20']
const MIN_WITHDRAW = 25

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n) { return Number(n).toLocaleString() }
function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}
function shortAddr(addr) {
    if (!addr || addr.length < 12) return addr
    return addr.slice(0, 8) + '…' + addr.slice(-6)
}
function timeAgo(ts) {
    const diff = Date.now() - (ts instanceof Date ? ts.getTime() : ts)
    const mins  = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days  = Math.floor(diff / 86400000)
    if (mins < 1)   return 'just now'
    if (mins < 60)  return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
}

// Map MongoDB transaction types → icon/colour used in the history UI
const TX_ICONS = {
    deposit:            { icon: '⬇', color: '#1D9E75', bg: '#E1F5EE' },
    withdraw:           { icon: '⬆', color: '#A32D2D', bg: '#FCEBEB' },
    win:                { icon: '🏆', color: '#633806', bg: '#FAEEDA' },
    loss:               { icon: '🎱', color: '#5F5E5A', bg: '#F1EFE8' },
    wager:              { icon: '🎱', color: '#5F5E5A', bg: '#F1EFE8' },
    refund:             { icon: '↩',  color: '#0C447C', bg: '#E6F1FB' },
    host_bonus:         { icon: '💎', color: '#085041', bg: '#E1F5EE' },
    tournament_entry:   { icon: '🎟', color: '#0C447C', bg: '#E6F1FB' },
    tournament_payout:  { icon: '💎', color: '#085041', bg: '#E1F5EE' },
    fee:                { icon: '💸', color: '#5F5E5A', bg: '#F1EFE8' },
}
const POSITIVE_TYPES = new Set(['deposit', 'win', 'refund', 'host_bonus', 'tournament_payout'])

// ─── QR placeholder ───────────────────────────────────────────────────────────
function QRPlaceholder({ value }) {
    return (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <div style={{ display: 'inline-block', background: 'white', padding: 12, borderRadius: 8, border: '0.5px solid var(--color-border-tertiary)' }}>
                <svg width="140" height="140" viewBox="0 0 140 140" xmlns="http://www.w3.org/2000/svg">
                    <rect width="140" height="140" fill="white"/>
                    <rect x="4"   y="4"   width="42" height="42" rx="3" fill="#111" />
                    <rect x="8"   y="8"   width="34" height="34" rx="2" fill="white"/>
                    <rect x="12"  y="12"  width="26" height="26" rx="1" fill="#111"/>
                    <rect x="94"  y="4"   width="42" height="42" rx="3" fill="#111"/>
                    <rect x="98"  y="8"   width="34" height="34" rx="2" fill="white"/>
                    <rect x="102" y="12"  width="26" height="26" rx="1" fill="#111"/>
                    <rect x="4"   y="94"  width="42" height="42" rx="3" fill="#111"/>
                    <rect x="8"   y="98"  width="34" height="34" rx="2" fill="white"/>
                    <rect x="12"  y="102" width="26" height="26" rx="1" fill="#111"/>
                    {[52,58,64,70,76,82,88].map((x,i) =>
                        [52,58,64,70,76,82,88].map((y,j) =>
                            ((i+j)%2===0) ? <rect key={x+'-'+y} x={x} y={y} width="4" height="4" fill="#111"/> : null
                        )
                    )}
                    <rect x="58" y="58" width="24" height="24" rx="4" fill="#1D9E75"/>
                    <text x="70" y="75" textAnchor="middle" fontSize="14" fill="white">💎</text>
                </svg>
                <div style={{ fontSize: 10, color: '#888', marginTop: 6, maxWidth: 140, wordBreak: 'break-all' }}>
                    {value.slice(0, 20)}…
                </div>
            </div>
        </div>
    )
}

// ─── Auth Screen ──────────────────────────────────────────────────────────────
function AuthScreen({ onAuthed }) {
    const [mode,        setMode]        = useState('signin')
    const [email,       setEmail]       = useState('')
    const [password,    setPassword]    = useState('')
    const [displayName, setDisplayName] = useState('')
    const [loading,     setLoading]     = useState(false)
    const [error,       setError]       = useState('')

    async function handleSubmit() {
        setError('')
        if (!email || !password) { setError('Email and password are required'); return }
        if (mode === 'signup' && !displayName) { setError('Display name is required'); return }
        setLoading(true)
        const { auth, db } = getFirebase()
        try {
            let firebaseUser
            if (mode === 'signup') {
                const cred = await createUserWithEmailAndPassword(auth, email, password)
                firebaseUser = cred.user
                await updateProfile(firebaseUser, { displayName })
                // Seed presence profile in Firebase RTDB (display only, not authoritative)
                await set(ref(db, `users/${firebaseUser.uid}/profile`), {
                    displayName,
                    email,
                    createdAt: Date.now(),
                })
            } else {
                const cred = await signInWithEmailAndPassword(auth, email, password)
                firebaseUser = cred.user
            }

            // Exchange Firebase ID token for ClubSwap JWT → MongoDB account
            const idToken = await firebaseUser.getIdToken()
            const data    = await authAPI.loginWithFirebase(idToken)
            setToken(data.token)
            setMongoId(data.user.id)

            // Store mongoId in Firebase profile so other users can look it up
            await update(ref(db, `users/${firebaseUser.uid}/profile`), {
                mongoId:     data.user.id,
                displayName: data.user.username,
                level:       data.user.level || 1,
            })

            onAuthed(firebaseUser, data.user)
        } catch (e) {
            const msgs = {
                'auth/email-already-in-use': 'Email already registered — sign in instead',
                'auth/wrong-password':       'Wrong password',
                'auth/user-not-found':       'No account with that email',
                'auth/weak-password':        'Password must be at least 6 characters',
                'auth/invalid-email':        'Invalid email address',
                'auth/invalid-credential':   'Wrong email or password',
            }
            setError(msgs[e.code] || e.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div style={S.authWrap}>
            <div style={S.authCard}>
                <div style={S.authLogo}>💎</div>
                <div style={S.authTitle}>ClubSwap</div>
                <div style={S.authSub}>{mode === 'signin' ? 'Sign in to your account' : 'Create your account'}</div>

                <div style={S.authTabs}>
                    <button style={{ ...S.authTab, ...(mode === 'signin' ? S.authTabActive : {}) }} onClick={() => { setMode('signin'); setError('') }}>Sign in</button>
                    <button style={{ ...S.authTab, ...(mode === 'signup' ? S.authTabActive : {}) }} onClick={() => { setMode('signup'); setError('') }}>Sign up</button>
                </div>

                {mode === 'signup' && (
                    <div style={S.fieldRow}>
                        <div style={S.fieldLabel}>Display name</div>
                        <input style={S.input} placeholder="e.g. PoolShark99" value={displayName} onChange={e => setDisplayName(e.target.value)} />
                    </div>
                )}
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Email</div>
                    <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Password</div>
                    <input style={S.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
                </div>

                {error && <div style={S.errorMsg}>{error}</div>}

                <button style={S.btnPrimary} onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
                </button>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────
//  Deposit Tab
// ─────────────────────────────────────────────────────────────
function DepositTab({ uid }) {
    const [network,  setNetwork]  = useState('ERC20')
    const [pkg,      setPkg]      = useState(null)   // selected package
    const navigate = useNavigate()
    const [step,     setStep]     = useState(1)      // 1=select, 2=address
    const [copied,   setCopied]   = useState(false)

    const address = DEPOSIT_ADDRESSES[network]

    function handleContinue() {
        if (!pkg) return
        navigate("/deposit", { state: { package: pkg } })
    }

    function copyAddr() {
        navigator.clipboard && navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    async function confirmSent() {
        // Write a pending record to Firebase so admin can see it and confirm on-chain.
        // Admin then calls POST /api/wallet/deposit (backend) to credit diamonds.
        const { db } = getFirebase()
        const { ref: fbRef, push } = await import('firebase/database')
        await push(fbRef(db, `depositRequests/${uid}`), {
            diamonds:  pkg.diamonds,
            usdAmount: pkg.usdPrice,
            network,
            status:    'pending',
            createdAt: Date.now(),
            note:      'Awaiting blockchain confirmation',
        })
        alert(`Deposit of ${pkg.diamonds} 💎 submitted — will be credited once the transaction confirms on-chain.`)
        setStep(1)
        setPkg(null)
    }

    return (
        <div>
            {step === 1 && (
                <>
                    <div style={S.card}>
                        <div style={S.secLabel}>1. Select network</div>
                        <div style={S.netGrid}>
                            {NETWORKS.map(n => (
                                <button
                                    key={n}
                                    style={{ ...S.netOpt, ...(network === n ? S.netOptSel : {}), ...(n === 'BEP20' ? { opacity: 0.4, cursor: 'not-allowed', filter: 'grayscale(1)' } : {}) }}
                                    onClick={() => n !== 'BEP20' && setNetwork(n)}
                                    disabled={n === 'BEP20'}
                                >
                                    <div style={S.netIcon}>{n === 'Polygon POS' ? '🟣' : '🟡'}</div>
                                    <div style={S.netName}>{n}</div>
                                    <div style={S.netSub}>{n === 'Polygon POS' ? 'Polygon network' : 'Coming soon'}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={S.card}>
                        <div style={S.secLabel}>2. Select diamonds to buy</div>
                        <div style={S.pkgList}>
                            {DIAMOND_PACKAGES.map(p => (
                                <button key={p.diamonds} style={{ ...S.pkgOpt, ...(pkg?.diamonds === p.diamonds ? S.pkgOptSel : {}) }} onClick={() => setPkg(p)}>
                                    <div style={S.pkgLeft}>
                                        <span style={S.pkgGem}>💎</span>
                                        <span style={S.pkgDia}>{fmt(p.diamonds)}</span>
                                        <span style={S.pkgDiaLbl}>diamonds</span>
                                    </div>
                                    <div style={S.pkgPrice}>${p.usdPrice.toFixed(2)}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={S.infoBox}>
                        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                            Once the funds are sent and the transaction is confirmed, your diamond balance will be credited automatically.
                            Please do not send less than the selected amount. Account for blockchain network fees.
                        </div>
                    </div>

                    <button style={{ ...S.btnPrimary, opacity: pkg ? 1 : 0.5, cursor: pkg ? 'pointer' : 'not-allowed' }} onClick={() => pkg && setStep(2)} disabled={!pkg}>
                        Continue →
                    </button>
                </>
            )}

            {step === 2 && pkg && (
                <>
                    <div style={{ ...S.card, border: '2px solid #1D9E75' }}>
                        <div style={S.secLabel}>3. Send payment to this address</div>
                        <div style={S.depositSummary}>
                            <div style={S.depositSumRow}><span>You send</span><strong>${pkg.usdPrice.toFixed(2)} USD</strong></div>
                            <div style={S.depositSumRow}><span>You receive</span><strong style={{ color: '#1D9E75' }}>{fmt(pkg.diamonds)} 💎</strong></div>
                            <div style={S.depositSumRow}><span>Network</span><strong>{network}</strong></div>
                        </div>
                        <div style={S.divider} />
                        <QRPlaceholder value={address} />
                        <div style={S.addrBox}>
                            <div style={S.addrText}>{address}</div>
                            <button style={S.copyBtn} onClick={copyAddr}>{copied ? '✓' : '📋'}</button>
                        </div>
                        <div style={S.warningBox}>
                            ⚠ Only send on the <strong>{network}</strong> network. Sending on the wrong network will result in permanent loss of funds.
                        </div>
                    </div>
                    <button style={S.btnPrimary} onClick={confirmSent}>I've sent the payment →</button>
                    <button style={S.btnSecondary} onClick={() => setStep(1)}>← Back</button>
                </>
            )}
        </div>
    )
}

// ─── Withdraw Tab ─────────────────────────────────────────────────────────────
function WithdrawTab({ balance, onBalanceChange }) {
    const [network,  setNetwork]  = useState('ERC20')
    const [amount,   setAmount]   = useState('')
    const [toAddr,   setToAddr]   = useState('')
    const [loading,  setLoading]  = useState(false)
    const [error,    setError]    = useState('')
    const [success,  setSuccess]  = useState(false)

    async function handleWithdraw() {
        setError('')
        const amt = parseInt(amount)
        if (!toAddr.trim()) { setError('Enter your wallet address'); return }
        if (!amt || amt < MIN_WITHDRAW) { setError(`Minimum withdrawal is ${MIN_WITHDRAW} 💎`); return }
        if (amt > balance) { setError('Insufficient diamond balance'); return }
        setLoading(true)
        try {
            // POST /api/wallet/withdraw — MongoDB deducts balance atomically
            await walletAPI.withdraw(amt, toAddr.trim())
            onBalanceChange()   // refresh balance from MongoDB
            setSuccess(true)
            setAmount('')
            setToAddr('')
        } catch (e) {
            setError('Withdrawal failed: ' + e.message)
        } finally {
            setLoading(false)
        }
    }

    if (success) return (
        <div style={{ ...S.card, textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--color-text-primary)' }}>Withdrawal submitted</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 6 }}>
                Usually processed within 24 hours. You can track it in History.
            </div>
            <button style={{ ...S.btnPrimary, marginTop: 20 }} onClick={() => setSuccess(false)}>Make another withdrawal</button>
        </div>
    )

    return (
        <div>
            <div style={S.card}>
                <div style={S.secLabel}>Your balance</div>
                <div style={{ fontSize: 28, fontWeight: 500, color: '#1D9E75', margin: '8px 0' }}>{fmt(balance)} 💎</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Minimum withdrawal: {MIN_WITHDRAW} 💎</div>
            </div>

            <div style={S.card}>
                <div style={S.secLabel}>Withdraw to</div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Network</div>
                    <div style={S.netGrid}>
                        {NETWORKS.map(n => (
                            <button key={n} style={{ ...S.netOpt, ...(network === n ? S.netOptSel : {}) }} onClick={() => setNetwork(n)}>
                                <div style={S.netIcon}>{n === 'ERC20' ? '⟠' : '🔵'}</div>
                                <div style={S.netName}>{n}</div>
                                <div style={S.netSub}>{n === 'ERC20' ? 'Ethereum · Polygon' : 'Base network'}</div>
                            </button>
                        ))}
                    </div>
                </div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Your {network} wallet address</div>
                    <input style={S.input} placeholder="0x..." value={toAddr} onChange={e => setToAddr(e.target.value)} />
                </div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Amount (💎 diamonds)</div>
                    <input style={S.input} type="number" min={MIN_WITHDRAW} max={balance} placeholder={`Min ${MIN_WITHDRAW}`}
                        value={amount} onChange={e => setAmount(e.target.value)} />
                    {amount && parseInt(amount) >= MIN_WITHDRAW && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                            ≈ ${(parseInt(amount) * 0.065).toFixed(2)} USD estimated
                        </div>
                    )}
                </div>
                <div style={S.warningBox}>⚠ Double-check your wallet address. Withdrawals sent to wrong addresses cannot be recovered.</div>
            </div>

            {error && <div style={S.errorMsg}>{error}</div>}

            <button
                style={{ ...S.btnPrimary, opacity: (toAddr && amount && parseInt(amount) >= MIN_WITHDRAW) ? 1 : 0.5 }}
                onClick={handleWithdraw}
                disabled={loading}
            >
                {loading ? 'Processing…' : `Withdraw ${amount || '—'} 💎`}
            </button>
        </div>
    )
}

// ─── History Tab ──────────────────────────────────────────────────────────────
function HistoryTab({ uid }) {
    const [history, setHistory] = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState('')

    useEffect(() => {
        const { db } = getFirebase()
        const hRef = ref(db, `users/${uid}/history`)
        onValue(hRef, snap => {
            setLoading(false)
            if (!snap.exists()) { setHistory([]); return }
            const items = Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            items.sort((a, b) => (b.timestamp || b.createdAt || 0) - (a.timestamp || a.createdAt || 0))
            setHistory(items)
        })
        return () => off(hRef)
    }, [uid])

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading…</div>
    if (error)   return <div style={{ ...S.card, color: '#A32D2D', padding: '24px 20px' }}>{error}</div>

    if (!history.length) return (
        <div style={{ ...S.card, textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--color-text-primary)' }}>No transactions yet</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>Deposit diamonds to get started</div>
        </div>
    )

    return (
        <div style={S.card}>
            <div style={S.secLabel}>Transaction history</div>
            {history.map((tx, i) => {
                const style = TX_ICONS[tx.type] || { icon: '💫', color: '#085041', bg: '#E1F5EE' }
                const isPositive = ['deposit', 'win', 'payout'].includes(tx.type)
                return (
                    <div key={tx._id} style={{ ...S.txRow, borderBottom: i < history.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none' }}>
                        <div style={{ ...S.txIcon, background: style.bg, color: style.color }}>{style.icon}</div>
                        <div style={{ flex: 1 }}>
                            <div style={S.txType}>{tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}</div>
                            <div style={S.txNote}>{tx.description || tx.note || tx.network || ''}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ ...S.txAmount, color: isPositive ? '#1D9E75' : '#A32D2D' }}>
                                {isPositive ? '+' : '-'}{fmt(Math.abs(tx.amount ?? tx.diamonds ?? 0))} 💎
                            </div>
                            <div style={S.txMeta}>
                                <span style={{ ...S.txStatus, background: tx.status === 'pending' ? '#FAEEDA' : '#E1F5EE', color: tx.status === 'pending' ? '#633806' : '#085041' }}>
                                    {tx.status || 'confirmed'}
                                </span>
                                <span style={{ marginLeft: 6 }}>{timeAgo(tx.timestamp || tx.createdAt || Date.now())}</span>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// ─── Profile Tab ──────────────────────────────────────────────────────────────
function ProfileTab({ firebaseUser, mongoUser, profile, onProfileUpdate }) {
    const [displayName, setDisplayName] = useState(profile?.displayName || mongoUser?.username || '')
    const [wallet,      setWallet]      = useState(profile?.wallet || '')
    const [saving,      setSaving]      = useState(false)
    const [saved,       setSaved]       = useState(false)

    async function save() {
        setSaving(true)
        const { db, auth } = getFirebase()
        await update(ref(db, `users/${firebaseUser.uid}/profile`), { displayName, wallet })
        await updateProfile(auth.currentUser, { displayName })
        setSaving(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
        onProfileUpdate && onProfileUpdate({ displayName, wallet })
    }

    async function handleSignOut() {
        const { auth } = getFirebase()
        clearToken()
        await signOut(auth)
    }

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={S.bigAvatar}>{initials(displayName || firebaseUser.email)}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>{firebaseUser.email}</div>
            </div>

            <div style={S.card}>
                <div style={S.secLabel}>Profile</div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Display name</div>
                    <input style={S.input} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" />
                </div>
                <div style={S.fieldRow}>
                    <div style={S.fieldLabel}>Linked wallet address (optional)</div>
                    <input style={S.input} value={wallet} onChange={e => setWallet(e.target.value)} placeholder="0x..." />
                </div>
                <button style={{ ...S.btnPrimary, marginTop: 12 }} onClick={save} disabled={saving}>
                    {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save changes'}
                </button>
                <LinkTelegram uid={firebaseUser.uid} profile={profile} />
            </div>

            <div style={S.card}>
                <div style={S.secLabel}>Account</div>
                <div style={S.summaryRow}><span>Email</span><strong>{firebaseUser.email}</strong></div>
                <div style={S.summaryRow}><span>Level</span><strong>⭐ {mongoUser?.level || 1}</strong></div>
                <div style={S.summaryRow}><span>Member since</span><strong>{new Date(profile?.createdAt || Date.now()).toLocaleDateString()}</strong></div>
                <div style={S.divider} />
                <button style={{ ...S.btnSecondary, color: '#A32D2D', borderColor: '#F7C1C1', marginTop: 8 }} onClick={handleSignOut}>
                    Sign out
                </button>
            </div>
        </div>
    )
}

// ─── ROOT — UserAccount ───────────────────────────────────────────────────────
export default function UserAccount() {
    const [firebaseUser, setFirebaseUser] = useState(null)
    const [mongoUser,    setMongoUser]    = useState(null)
    const [profile,      setProfile]      = useState(null)
    const [balance,      setBalance]      = useState(0)
    const [escrow,       setEscrow]       = useState(0)
    const [tab,          setTab]          = useState('deposit')
    const [loading,      setLoading]      = useState(true)

    // Fetch authoritative balance from MongoDB
    const refreshBalance = useCallback(async () => {
        if (!getToken()) return
        try {
            const w = await walletAPI.balance()
            setBalance(w.balance)
            setEscrow(w.escrow)
        } catch {}
    }, [])

    // Firebase Auth listener
    useEffect(() => {
        const { auth, db } = getFirebase()
        const unsub = onAuthStateChanged(auth, async fbUser => {
            setFirebaseUser(fbUser)
            setLoading(false)
            if (!fbUser) {
                setMongoUser(null)
                setBalance(0)
                setEscrow(0)
                return
            }

            // Load Firebase display profile (non-financial, for UI only)
            const profSnap = await get(ref(db, `users/${fbUser.uid}/profile`))
            if (profSnap.exists()) setProfile(profSnap.val())

            // If we already have a JWT (from a previous call or page load), refresh balance
            if (getToken()) {
                refreshBalance()
            } else {
                // Re-exchange Firebase token on each page load to ensure JWT is fresh
                try {
                    const idToken = await fbUser.getIdToken()
                    const data    = await authAPI.loginWithFirebase(idToken)
                    setToken(data.token)
                    setMongoId(data.user.id)
                    setMongoUser(data.user)
                    // Sync mongoId back to Firebase profile
                    await update(ref(db, `users/${fbUser.uid}/profile`), {
                        mongoId: data.user.id,
                        level:   data.user.level || 1,
                    })
                    const w = await walletAPI.balance()
                    setBalance(w.balance)
                    setEscrow(w.escrow)
                } catch {}
            }
        })
        return () => unsub()
    }, [refreshBalance])

    // Called from AuthScreen after successful signup/signin
    function handleAuthed(fbUser, mUser) {
        setFirebaseUser(fbUser)
        setMongoUser(mUser)
        setLoading(false)
        refreshBalance()
    }

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: 'var(--color-text-secondary)' }}>
            Loading…
        </div>
    )

    if (!firebaseUser) return <AuthScreen onAuthed={handleAuthed} />

    const displayName = profile?.displayName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Player'

    const TABS = [
        { key: 'deposit',  label: '⬇ Deposit'  },
        { key: 'withdraw', label: '⬆ Withdraw' },
        { key: 'history',  label: '📋 History'  },
        { key: 'profile',  label: '👤 Profile'  },
    ]

    return (
        <div style={S.root}>
            {/* Balance bar */}
            <div style={S.balanceBar}>
                <div style={S.avatarSmall}>{initials(displayName)}</div>
                <div>
                    <div style={S.balanceName}>{displayName}</div>
                    <div style={S.balanceAmt}>{fmt(balance)} 💎 diamonds</div>
                    {escrow > 0 && (
                        <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                            {fmt(escrow)} 💎 in escrow
                        </div>
                    )}
                </div>
                {profile?.wallet && (
                    <div style={S.walletChip}>{shortAddr(profile.wallet)}</div>
                )}
            </div>

            {/* Tab nav */}
            <div style={S.tabNav}>
                {TABS.map(t => (
                    <button key={t.key} style={{ ...S.tabBtn, ...(tab === t.key ? S.tabBtnActive : {}) }} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {tab === 'deposit'  && <DepositTab  uid={firebaseUser.uid} />}
            {tab === 'withdraw' && <WithdrawTab balance={balance} onBalanceChange={refreshBalance} />}
            {tab === 'history'  && <HistoryTab uid={firebaseUser.uid} />}
            {tab === 'profile'  && (
                <ProfileTab
                    firebaseUser={firebaseUser}
                    mongoUser={mongoUser}
                    profile={profile}
                    onProfileUpdate={p => setProfile(prev => ({ ...prev, ...p }))}
                />
            )}
        </div>
    )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
    root: { maxWidth: 600, margin: '0 auto', padding: '20px 16px', fontFamily: 'var(--font-sans, system-ui, sans-serif)' },
    authWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', padding: 16 },
    authCard: { background: '#0a1a0c', border: '1px solid #1a3a1c', borderRadius: 16, padding: '32px 28px', width: '100%', maxWidth: 400 },
    authLogo: { fontSize: 36, textAlign: 'center', marginBottom: 6 },
    authTitle: { fontSize: 22, fontWeight: 500, textAlign: 'center', color: '#e8f5e9' },
    authSub: { fontSize: 13, textAlign: 'center', color: '#4a7a50', marginBottom: 20, marginTop: 4 },
    authTabs: { display: 'flex', gap: 0, marginBottom: 20, background: '#060f08', borderRadius: 10, padding: 4, border: '1px solid #1a3a1c' },
    authTab: { flex: 1, padding: '8px', border: 'none', background: 'transparent', borderRadius: 8, fontSize: 14, color: '#4a7a50', cursor: 'pointer', fontFamily: 'inherit' },
    authTabActive: { background: '#0d2210', color: '#4ade80', fontWeight: 700, border: '1px solid #1a4a1c' },

    // Balance bar
    balanceBar:  { display: 'flex', alignItems: 'center', gap: 12, background: '#0a1a0c', border: '1px solid #1a3a1c', borderRadius: 14, padding: '14px 18px', marginBottom: 16 },
    avatarSmall: { width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#1a4a1c,#2a6a2c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 500, color: 'white', flexShrink: 0 },
    balanceName: { fontSize: 14, fontWeight: 600, color: '#c8e8ca' },
    balanceAmt:  { fontSize: 18, fontWeight: 700, color: '#4ade80', marginTop: 2 },
    walletChip:  { marginLeft: 'auto', background: '#0a2a0c', border: '1px solid #1a4a1c', borderRadius: 20, padding: '4px 10px', fontSize: 11, color: '#4ade80', fontFamily: 'monospace' },

    // Tabs
    tabNav: { display: 'flex', gap: 0, marginBottom: 20, background: '#060f08', borderRadius: 10, padding: 4, border: '1px solid #1a3a1c' },
    tabBtn: { flex: 1, padding: '8px 4px', border: 'none', background: 'transparent', borderRadius: 8, fontSize: 12, color: '#4a7a50', cursor: 'pointer', fontFamily: 'inherit' },
    tabBtnActive: { background: '#0d2210', color: '#4ade80', fontWeight: 700, border: '1px solid #1a4a1c' },

    // Cards & fields
    card: { background: '#0a1a0c', border: '1px solid #1a3a1c', borderRadius: 14, padding: '16px 20px', marginBottom: 14 },
    secLabel: { fontSize: 10, fontWeight: 700, color: '#2a5a2c', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 },
    fieldRow: { marginBottom: 12 },
    fieldLabel: { fontSize: 12, color: '#4a7a50', marginBottom: 5, fontWeight: 600 },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #1a3a1c', borderRadius: 8, fontSize: 14, color: '#e8f5e9', background: '#060f08', boxSizing: 'border-box', outline: 'none', fontFamily: 'var(--font-sans)' },
    divider: { border: 'none', borderTop: '1px solid #1a3a1c', margin: '10px 0' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: 14, color: '#4a7a50' },

    // Network selector
    netGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 },
    netOpt:  { border: '1px solid #1a3a1c', borderRadius: 10, padding: '12px', cursor: 'pointer', background: '#060f08', textAlign: 'left' },
    netOptSel: { border: '2px solid #4ade80', background: '#0a2a0c' },
    netIcon: { fontSize: 20, marginBottom: 4 },
    netName: { fontSize: 14, fontWeight: 600, color: '#4ade80' },
    netSub:  { fontSize: 11, color: '#2a5a2c', marginTop: 2 },

    // Diamond packages
    pkgList: { display: 'flex', flexDirection: 'column', gap: 8 },
    pkgOpt:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #1a3a1c', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: '#060f08', width: '100%', fontFamily: 'inherit' },
    pkgOptSel: { border: '2px solid #4ade80', background: '#0a2a0c' },
    pkgLeft: { display: 'flex', alignItems: 'center', gap: 8 },
    pkgGem:  { fontSize: 20 },
    pkgDia:  { fontSize: 16, fontWeight: 600, color: '#e8f5e9' },
    pkgDiaLbl: { fontSize: 12, color: '#4a7a50' },
    pkgPrice: { fontSize: 15, fontWeight: 600, color: '#4ade80' },

    // Deposit step 2
    depositSummary: { marginBottom: 12 },
    depositSumRow:  { display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '5px 0', color: '#4a7a50' },
    addrBox:  { display: 'flex', alignItems: 'center', gap: 8, background: '#060f08', border: '1px solid #1a3a1c', borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
    addrText: { flex: 1, fontSize: 12, color: '#4ade80', wordBreak: 'break-all', fontFamily: 'monospace' },
    copyBtn:  { padding: '6px 10px', background: '#0a2a0c', border: '1px solid #1a4a1c', borderRadius: 6, fontSize: 14, cursor: 'pointer', color: '#4ade80' },

    // History
    txRow:    { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' },
    txIcon:   { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 },
    txType:   { fontSize: 14, fontWeight: 600, color: '#c8e8ca' },
    txNote:   { fontSize: 12, color: '#4a7a50', marginTop: 2 },
    txAmount: { fontSize: 15, fontWeight: 500 },
    txMeta:   { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 2, fontSize: 11, color: '#4a7a50' },
    txStatus: { padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 500 },

    // Profile
    bigAvatar: { width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#1a4a1c,#2a6a2c)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#4ade80', border: '2px solid rgba(74,222,128,0.3)' },

    // Buttons
    btnPrimary:   { width: '100%', padding: 12, background: '#4ade80', color: '#040d06', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'block', marginTop: 4 },
    btnSecondary: { width: '100%', padding: 11, background: 'transparent', color: '#4a7a50', border: '1px solid #1a3a1c', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', display: 'block' },

    // Misc
    infoBox:    { background: '#0a1a0c', border: '1px solid #1a3a1c', borderRadius: 8, padding: '12px 14px', marginBottom: 14, color: '#4a7a50', fontSize: 13 },
    warningBox: { background: '#1a1000', border: '1px solid #3a2a00', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#fbbf24', marginBottom: 12 },
    errorMsg:   { background: '#2a0a0a', border: '1px solid #5a1a1a', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#f87171', marginBottom: 10 },
}
